<script lang="ts">
export const checkboxCls = cva("checkbox duration-150 before:duration-150", {
	variants: {
		color: {
			default: null,
			neutral: "checkbox-neutral",
			primary: "checkbox-primary",
			secondary: "checkbox-secondary",
			accent: "checkbox-accent",
			info: "checkbox-info",
			warning: "checkbox-warning",
			error: "checkbox-error",
		},
		size: {
			xs: "checkbox-xs",
			sm: "checkbox-sm",
			md: "checkbox-md",
			lg: "checkbox-lg",
		},
	},
	defaultVariants: {
		color: "default",
		size: "md",
	},
});

type _props = VariantProps<typeof checkboxCls>;

export interface CheckboxProps extends ClassProps, DeprecatedSizeProps, CheckboxRootProps {
	color?: _props["color"];
	size?: _props["size"];
}
</script>
<script setup lang="ts">
import { cva, type VariantProps } from "class-variance-authority";
import {
	CheckboxRoot,
	useForwardPropsEmits,
	type CheckboxRootEmits,
	type CheckboxRootProps,
} from "reka-ui";
import { computed } from "vue";
import { useDelegatedProps, type ClassProps } from "../../lib/base-props";
import { allSize, convertDeprecatedSize, type DeprecatedSizeProps } from "../../lib/size";
import { cn } from "../../lib/utils";

defineOptions({ inheritAttrs: false });

const props = defineProps<CheckboxProps>();
const emit = defineEmits<CheckboxRootEmits>();

const delegatedProps = useDelegatedProps(props, ["color", "size", ...allSize]);

const forwardedProps = useForwardPropsEmits(delegatedProps, emit);

const size = computed(() => {
	if (props.size != null) {
		return props.size;
	}

	return convertDeprecatedSize(props);
});
</script>

<template>
	<CheckboxRoot :class="cn(checkboxCls({ ...props, size }))" v-bind="forwardedProps" />
</template>
